<template>
  <div class="d-flex justify-end align-center">
    <!-- Admin-only "random real query" dice (oxjob #369) -->
    <serp-dice-button />

    <!-- Widgets (hidden during semantic search — group_by not supported) -->
    <action-menu v-if="isWorks && !isSemanticSearch" action="group_by">
      <template #activator="{ props: menuProps }">
        <v-tooltip text="Widgets" location="bottom" aria-label="Widgets">
          <template #activator="{ props: tooltipProps }">
            <v-btn icon variant="text" v-bind="{ ...menuProps, ...tooltipProps }" aria-label="Widgets">
              <v-icon color="grey-darken-1">mdi-widgets</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
      </template>
    </action-menu>

    <!-- Download -->
    <v-menu location="bottom">
      <template #activator="{ props: menuProps }">
        <v-tooltip :text="downloadTooltip" location="bottom" :aria-label="downloadTooltip">
          <template #activator="{ props: tooltipProps }">
            <v-btn icon variant="text" v-bind="{ ...menuProps, ...tooltipProps }" :aria-label="downloadTooltip">
              <v-icon color="grey-darken-1">mdi-tray-arrow-down</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
      </template>
      <v-list>
        <v-list-item @click="handleDownloadClick">
          <template #prepend>
            <v-icon>mdi-file-download-outline</v-icon>
          </template>
          <v-list-item-title>
            <template v-if="isSelectionScopedDownload">
              Export {{ exportSelection.count }} selected {{ filters.pluralize(entityType, exportSelection.count) }}
            </template>
            <template v-else>
              Export {{ formattedResultsCount }} {{ filters.pluralize(entityType, 2) }}
            </template>
          </v-list-item-title>
        </v-list-item>
        <v-list-item v-if="isWorks && groupByCount > 0 && !isSemanticSearch" :href="csvUrl">
          <template #prepend>
            <v-icon>mdi-chart-box-outline</v-icon>
          </template>
          <v-list-item-title>
            Export {{ groupByCount }} facets
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <!-- Three-dots menu -->
    <v-menu location="bottom" v-model="isDotsMenuOpen">
      <template #activator="{ props }">
        <v-btn icon variant="text" v-bind="props" aria-label="More options">
          <v-icon color="grey-darken-1">mdi-dots-vertical</v-icon>
        </v-btn>
      </template>
      <v-list min-width="260">
        <!-- View toggle: shown for ALL entity types. Table view (and per-entity
             columns) now works everywhere (#304), and the export dialog's
             "Open in table view" can land non-works users in table view — so they
             need this toggle to get back to list view. (Was works-only from the
             #295 era when table view was works-only.) -->
        <v-list-item @click="handleToggleTableView">
          <template #prepend>
            <v-icon>{{ url.isTableView($route) ? 'mdi-format-list-checkbox' : 'mdi-table' }}</v-icon>
          </template>
          <v-list-item-title>{{ url.isTableView($route) ? 'View as list' : 'View as table' }}</v-list-item-title>
        </v-list-item>

        <!-- Page size: hover submenu picking results per page. Always enabled —
             table view auto-selects 100 but you can still drop to 10/20/50. -->
        <v-menu
          submenu
          open-on-hover
          location="end"
          :offset="2"
        >
          <template #activator="{ props: subProps }">
            <!-- Drop the activator's click-toggle and swallow the click: the
                 submenu opens on hover, so a click here should do nothing — not
                 dismiss the kebab (Vuetify menus close on content click). Other
                 rows keep their default click-to-act-and-close behavior. -->
            <v-list-item
              v-bind="{ ...subProps, onClick: undefined }"
              @click.stop
            >
              <template #prepend>
                <v-icon>mdi-counter</v-icon>
              </template>
              <v-list-item-title>Page size</v-list-item-title>
              <template #append>
                <span class="text-medium-emphasis mr-2">{{ url.getPerPage() }}</span>
                <v-icon size="20">mdi-chevron-right</v-icon>
              </template>
            </v-list-item>
          </template>
          <v-list density="compact" min-width="120">
            <v-list-item
              v-for="size in url.pageSizeOptions"
              :key="size"
              @click="url.setPerPage(size)"
            >
              <template #prepend>
                <v-icon
                  size="18"
                  :style="{ visibility: size === url.getPerPage() ? 'visible' : 'hidden' }"
                >mdi-check</v-icon>
              </template>
              <v-list-item-title>{{ size }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-divider v-if="isWorks" />
        <v-list-item v-if="isWorks" @click="handleSaveToggle">
          <template #prepend>
            <v-icon>{{ activeSearchObj ? 'mdi-star' : 'mdi-star-outline' }}</v-icon>
          </template>
          <v-list-item-title>{{ activeSearchObj ? 'Search is saved' : 'Save search' }}</v-list-item-title>
        </v-list-item>
        <v-list-item v-if="isWorks && canAlert" @click="handleAlertToggle">
          <template #prepend>
            <v-icon>{{ activeSearchObj?.has_alert ? 'mdi-bell' : 'mdi-bell-outline' }}</v-icon>
          </template>
          <v-list-item-title>{{ activeSearchObj?.has_alert ? 'Alert is active' : 'Create alert' }}</v-list-item-title>
        </v-list-item>
        <v-divider v-if="isWorks" />
        <v-list-item @click="copyApiCall">
          <template #prepend>
            <v-icon>mdi-api</v-icon>
          </template>
          <v-list-item-title>Copy API call</v-list-item-title>
        </v-list-item>
        <v-list-item @click="openQrCode">
          <template #prepend>
            <v-icon>mdi-qrcode</v-icon>
          </template>
          <v-list-item-title>Get QR code</v-list-item-title>
        </v-list-item>

        <!-- Read-only "Show as OQL" toggle (oxjob #346) — behind the `oql`
             feature flag, so it ships dark. Toggles the OQL render panel in the
             right column; parent (ExpertSerp) owns + persists the state. -->
        <template v-if="oqlFlag">
          <v-divider />
          <v-list-item @click="handleToggleOql">
            <template #prepend>
              <v-icon>{{ showOql ? 'mdi-check' : 'mdi-code-tags' }}</v-icon>
            </template>
            <v-list-item-title>{{ showOql ? 'Hide OQL' : 'Show OQL' }}</v-list-item-title>
          </v-list-item>
        </template>
      </v-list>
    </v-menu>

    <!-- Hidden export button (dialogs are teleported, so they still work) -->
    <serp-results-export-button ref="exportButtonRef" class="d-none" />

    <!-- Unsave confirmation dialog -->
    <v-dialog v-model="isDialogOpen.unsaveConfirm" max-width="400">
      <v-card rounded>
        <v-card-title>Unsave this search?</v-card-title>
        <v-card-text>
          This will remove the saved search{{ activeSearchObj?.has_alert ? ' and its alert' : '' }}.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" rounded @click="isDialogOpen.unsaveConfirm = false">Cancel</v-btn>
          <v-btn variant="flat" rounded color="primary" @click="confirmUnsave">Unsave</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Remove alert confirmation dialog -->
    <v-dialog v-model="isDialogOpen.removeAlertConfirm" max-width="400">
      <v-card rounded>
        <v-card-title>Remove this alert?</v-card-title>
        <v-card-text>
          You'll stop receiving alerts for this search. The saved search will not be deleted.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" rounded @click="isDialogOpen.removeAlertConfirm = false">Cancel</v-btn>
          <v-btn variant="flat" rounded color="primary" @click="confirmRemoveAlert">Remove</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Login required dialog -->
    <v-dialog v-model="isDialogOpen.loginRequired" max-width="500">
      <v-card rounded>
        <v-card-title>Login required</v-card-title>
        <v-card-text>
          To save searches and create alerts, please log in or sign up.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" rounded @click="clickLogin">Log in</v-btn>
          <v-btn rounded color="primary" @click="clickSignup">Sign up</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- QR Code dialog -->
    <v-dialog :width="qrCodeSize" v-model="isDialogOpen.qrCode">
      <v-card rounded>
        <v-toolbar flat>
          <v-toolbar-title>QR code for this page:</v-toolbar-title>
          <v-spacer/>
        </v-toolbar>
        <v-card-text v-if="isUrlTooBigForQR">
          <v-alert type="warning" text>
            Your current URL is too long to create a QR code.
          </v-alert>
        </v-card-text>
        <qrcode-vue v-else :value="urlToShare" :size="qrCodeSize" />
        <v-card-actions>
          <v-spacer/>
          <v-btn color="primary" rounded @click="isDialogOpen.qrCode = false">Dismiss</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>


<script setup>
import { ref, computed, reactive } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { canAlertOnFilter } from '@/collectionFilter';
import { legacyApiUrl } from '@/collectionImportSource';
import { useDisplay } from 'vuetify';
import QrcodeVue from 'qrcode.vue';

import { url } from '@/url';
import filters from '@/filters';
import { filtersFromUrlStr } from '@/filterConfigs';
import { entityConfigs } from '@/entityConfigs';
import { urlBase } from '@/apiConfig';
import { exportToCsv } from '@/utils/csvExport';
import { resolveExportSelection } from '@/utils/selectionExport';

import ActionMenu from '@/components/Action/ActionMenu.vue';
import SerpResultsExportButton from '@/components/SerpResultsExportButton.vue';
import SerpDiceButton from '@/components/SerpDiceButton.vue';

defineOptions({ name: 'SerpRightToolbar' });

const props = defineProps({
  resultsObject: Object,
  showOql: Boolean,
});

const emit = defineEmits(['toggle-oql']);

const store = useStore();
const route = useRoute();
// No alert item on a works-collection search: it can never gain new works (oxjob #1505).
const canAlert = computed(() => canAlertOnFilter(route.query.filter));
const router = useRouter();
const { mdAndUp } = useDisplay();

const exportButtonRef = ref(null);
const isDotsMenuOpen = ref(false);
const isDialogOpen = reactive({
  unsaveConfirm: false,
  removeAlertConfirm: false,
  loginRequired: false,
  qrCode: false,
});

// Store getters
const entityType = computed(() => store.getters.entityType);
const isWorks = computed(() => entityType.value === 'works');
const isSemanticSearch = computed(() => !!route.query['search.semantic']);
const activeSearchObj = computed(() => store.getters['user/activeSearchObj']);
const userId = computed(() => store.getters['user/userId']);
const oqlFlag = computed(() => !!store.getters.featureFlags['oql']);

// Results count
const formattedResultsCount = computed(() => {
  const count = props.resultsObject?.meta?.count;
  return count ? filters.toPrecision(count) : '0';
});

// When specific rows are ticked, the download is scoped to just those (ZD #8373
// / #388) — but only on async-export entities, where the export dialog actually
// honors the ids filter. Client-mode (taxonomy) entities export the full list,
// so their label must keep showing the total to stay honest.
const exportSelection = computed(() => resolveExportSelection(store.state.selection));
const isSelectionScopedDownload = computed(
  () => exportMode.value === 'async' && exportSelection.value.scoped
);

// Export-button tooltip mirrors the menu item (and the sibling
// SerpDownloadButton on the OQL SERP): when rows are ticked, name the scope so
// the user can see the export is limited to their selection before opening it.
const downloadTooltip = computed(() =>
  isSelectionScopedDownload.value
    ? `Export ${exportSelection.value.count} selected ${filters.pluralize(entityType.value, exportSelection.value.count)}`
    : `Export ${formattedResultsCount.value} ${filters.pluralize(entityType.value, 2)}`
);

// Group-by / facets
const groupByKeys = computed(() => {
  const keys = url.getGroupBy(route);
  keys.sort((a) => ['apc_sum', 'cited_by_count_sum'].includes(a) ? -1 : 1);
  return keys;
});

const groupByCount = computed(() => groupByKeys.value.length);

const csvUrl = computed(() => {
  const myFilters = filtersFromUrlStr(entityType.value, route.query.filter);
  return url.makeGroupByUrl(
    entityType.value,
    groupByKeys.value.join(','),
    { filters: myFilters, isMultipleGroups: true, formatCsv: true }
  );
});

// QR code
const urlToShare = computed(() => `https://openalex.org${route.fullPath}`);
const isUrlTooBigForQR = computed(() => urlToShare.value.length > 3000);
const qrCodeSize = computed(() => mdAndUp.value ? 400 : 300);

// API URL for copy
const apiCallUrl = computed(() => legacyApiUrl(route.query, entityType.value));

const exportMode = computed(() => entityConfigs[entityType.value]?.exportMode || 'async');

const snackbar = (val) => store.commit('snackbar', val);

// Export handling
function handleDownloadClick() {
  if (exportMode.value === 'client') {
    handleClientSideExport();
  } else {
    exportButtonRef.value?.openExportDialog();
  }
}

async function handleClientSideExport() {
  const config = entityConfigs[entityType.value];
  const columns = config?.exportColumns;
  if (!columns) return;

  snackbar('Exporting...');

  const params = { mailto: 'ui@openalex.org' };
  if (route.query.filter) params.filter = route.query.filter;
  if (route.query.search) params.search = route.query.search;
  if (route.query.sort) params.sort = route.query.sort;

  const date = new Date().toISOString().split('T')[0];
  const filename = `openalex_${entityType.value}_${date}.csv`;

  try {
    const count = await exportToCsv({
      url: `${urlBase.api}/${entityType.value}`,
      params,
      columns,
      filename,
      perPage: 200,
      maxPages: 10,
      axiosOptions: {},
    });
    snackbar(`Exported ${count} ${entityType.value}.`);
  } catch (e) {
    console.error('Client-side export failed:', e);
    snackbar('Export failed. Please try again.');
  }
}

// Save/alert auto-name
function generateAutoName() {
  const searchQuery = route.query.search
    || route.query['search.exact']
    || route.query['search.semantic']
    || route.query['search.title']
    || route.query['search.title.exact']
    || route.query['search.title_and_abstract']
    || route.query['search.title_and_abstract.exact']
    || route.query['search.title_abstract_keywords']
    || route.query['search.title_abstract_keywords.exact'];
  if (searchQuery) return searchQuery;
  const filterParam = route.query.filter;
  if (filterParam) {
    const filterKeys = filterParam.split(',').map(f => {
      const key = f.split(':')[0];
      return key.replace(/_/g, ' ').replace(/\./g, ' > ');
    });
    return filterKeys.slice(0, 3).join(', ');
  }
  return 'Untitled search';
}

// Save toggle
function handleToggleTableView() {
  isDotsMenuOpen.value = false;
  url.setResultsView(url.isTableView(route) ? 'list' : 'table');
}

async function handleSaveToggle() {
  isDotsMenuOpen.value = false;
  if (!userId.value) {
    isDialogOpen.loginRequired = true;
    return;
  }
  if (activeSearchObj.value) {
    isDialogOpen.unsaveConfirm = true;
  } else {
    await store.dispatch('user/createSearch', {
      search_url: 'https://openalex.org' + route.fullPath,
      name: generateAutoName(),
    });
  }
}

// Alert toggle
async function handleAlertToggle() {
  isDotsMenuOpen.value = false;
  if (!userId.value) {
    isDialogOpen.loginRequired = true;
    return;
  }
  if (activeSearchObj.value?.has_alert) {
    isDialogOpen.removeAlertConfirm = true;
  } else if (activeSearchObj.value) {
    await store.dispatch('user/updateSearchAlert', {
      id: activeSearchObj.value.id,
      has_alert: true,
    });
  } else {
    await store.dispatch('user/createSearch', {
      search_url: 'https://openalex.org' + route.fullPath,
      name: generateAutoName(),
      has_alert: true,
    });
  }
}

async function confirmUnsave() {
  isDialogOpen.unsaveConfirm = false;
  if (activeSearchObj.value) {
    await store.dispatch('user/unsaveCurrentSearch', activeSearchObj.value.id);
  }
}

async function confirmRemoveAlert() {
  isDialogOpen.removeAlertConfirm = false;
  if (activeSearchObj.value) {
    await store.dispatch('user/updateSearchAlert', {
      id: activeSearchObj.value.id,
      has_alert: false,
    });
  }
}

async function copyApiCall() {
  isDotsMenuOpen.value = false;
  await navigator.clipboard.writeText(apiCallUrl.value);
  snackbar('API URL copied to clipboard.');
}

function openQrCode() {
  isDotsMenuOpen.value = false;
  isDialogOpen.qrCode = true;
}

function handleToggleOql() {
  isDotsMenuOpen.value = false;
  emit('toggle-oql');
}

function clickLogin() {
  isDialogOpen.loginRequired = false;
  router.push({ name: 'Login', query: { redirect: route.fullPath } });
}

function clickSignup() {
  isDialogOpen.loginRequired = false;
  router.push({ name: 'Signup', query: { redirect: route.fullPath } });
}
</script>
